<template>
	<ClientOnly>
		<span
			v-if="show"
			:id="widgetId"
			:class="widgetClass"
			:data-ls-product-name="productName"
			:data-ls-brand="brand"
			:data-ls-product-id="productId"
			:data-ls-product-url="productUrl"
			:data-ls-sku="sku"
			:data-ls-description="description"
			:data-ls-image-url="imageUrl"
			:data-ls-price="price"
			:data-ls-price-currency="currency"
			:data-ls-availability="availability"
			:data-ls-category="category"
		/>
	</ClientOnly>
</template>

<script setup lang="ts">
import { resolveLipscoreProductId } from "~/utils/lipscore/resolveLipscoreProductId";
import {
	loadLipscoreScript,
	scheduleLipscoreReInit
} from "~/utils/lipscore/loadLipscoreScript";

interface Props {
	/**
	 * Which Lipscore rating widget to render. `small` is "Rating Small", built
	 * for category listings — stars only. `full` is "Rating", the product-page
	 * widget: it is the one Lipscore reads product data from for Google organic
	 * results and for the product XML feed, so a PDP wants `full`.
	 *
	 * The default stays `small` because this component has no overrides: the
	 * templates that have not signed off on the visual change keep today's look
	 * until they do, and switching one is a single prop.
	 */
	variant?: "small" | "full";
}

const { variant = "small" } = defineProps<Props>();

// Lipscore finds the two rating widgets differently, and getting this wrong
// renders nothing at all rather than failing loudly:
//   - "Rating Small" is scanned for by CLASS, so a page may carry many.
//   - "Rating" is picked up by `document.getElementById("lipscore-rating")`;
//     the class is what their script *adds* once it has found the element, so
//     setting the class alone leaves the widget empty. Being an id, it is also
//     a page singleton — only one widget per page may use `variant="full"`.
const widgetId = computed(() =>
	variant === "full" ? "lipscore-rating" : undefined
);

const widgetClass = computed(() =>
	variant === "full" ? "lipscore-rating" : "lipscore-rating-small"
);

const { selectedProduct } = storeToRefs(useProductStore());
const { currentCurrency } = storeToRefs(useNorceStore());
const { getProductStock } = useNorcePwa();
const requestURL = useRequestURL();
const route = useRoute();

const apiKey = useLipscoreApiKey();

const productName = computed(
	() => selectedProduct.value?.VariantName || selectedProduct.value?.Name || ""
);
const brand = computed(() => selectedProduct.value?.Manufacturer?.Name || "");
// [NF-350] Lipscore keys reviews on the Norce product code, resolved from the
// product group. There is deliberately no part-number fallback: sending an id
// Lipscore doesn't know makes it auto-create an empty product.
//
// [OR-10] An account whose reviews were collected under the part number is the
// other case, and it is a per-shop fact rather than a per-product one, so it is
// configured rather than guessed: `NORCE_LIPSCORE_ID_SOURCE=partNo`.
const { lipscoreIdSource } = useRuntimeConfig().public.norce;
const groupByKey = computed(() => selectedProduct.value?.GroupByKey || "");
const productId = ref("");
const { resolveId } = useLipscoreId();
const productUrl = computed(() => `${requestURL.origin}${route.fullPath}`);
const sku = computed(() => selectedProduct.value?.PartNo || "");
const description = computed(
	() =>
		selectedProduct.value?.SubHeader || selectedProduct.value?.Description || ""
);
const imageUrl = computed(() => selectedProduct.value?.Files?.[0]?.Path || "");
// Gated B2B guests must not get the price in the widget markup.
const { isPriceHidden } = usePriceVisibility();
const price = computed(() =>
	isPriceHidden.value ? undefined : selectedProduct.value?.PriceIncVat || 0
);
const currency = computed(() => currentCurrency.value?.Code || "SEK");
const availability = computed(() => {
	if (!selectedProduct.value) return 0;
	return Math.max(getProductStock(selectedProduct.value), 0);
});
const category = computed(() => {
	const paths = (selectedProduct.value as any)?.CategoryPaths as
		| Array<{ Name?: string }>
		| undefined;
	return (
		paths
			?.map((p) => p.Name)
			.filter(Boolean)
			.join("-") || ""
	);
});

const show = computed(
	() => !!apiKey.value && !!productName.value && !!productId.value
);

// A second `#lipscore-rating` is ignored by Lipscore rather than rejected: the
// id lookup above returns the first match and every other widget stays empty,
// with nothing logged. The component is placed by editors through Storyblok, so
// a duplicate arrives as a content change that no code review sees — say it out
// loud in dev instead.
const warnOnDuplicateWidget = () => {
	if (!import.meta.dev || variant !== "full") return;
	const count = document.querySelectorAll("#lipscore-rating").length;
	if (count < 2) return;
	console.warn(
		`[Lipscore] ${count} elements with id="lipscore-rating" on this page. ` +
			"Lipscore renders the first and leaves the rest empty — " +
			"only one widget per page may use the full variant."
	);
};

const lipscoreCode = useLipscoreCode();
// `force` only on a variant switch: the widget's data changed in place.
const loadScript = (key: string, force: boolean) =>
	loadLipscoreScript({
		apiKey: key,
		code: lipscoreCode.value,
		onReady: () => scheduleLipscoreReInit({ force })
	});

// Guards against a slow lookup for a previously selected product landing after
// a newer one and pairing product A's id with product B's name, price and sku.
let latestRequest = 0;

const initLipscore = async (force = false) => {
	const key = apiKey.value;
	const request = ++latestRequest;
	// Cleared first: this re-runs on every variant switch, and a stale id left
	// behind by an early return would misattribute the widget.
	productId.value = "";
	if (!key) return;
	const code = await resolveLipscoreProductId({
		source: lipscoreIdSource,
		partNo: sku.value,
		groupByKey: groupByKey.value,
		resolveProductCode: resolveId
	});
	if (request !== latestRequest) return;
	productId.value = code;
	if (!code) return;
	await nextTick();
	warnOnDuplicateWidget();
	loadScript(key, force);
};

onMounted(() => {
	if (import.meta.client) initLipscore();
});
// Watch the variant, not the group: the Lipscore id is product-level and comes
// from cache on a variant switch, but the other `data-ls-*` attributes (price,
// sku, availability) do change and the widget needs re-initialising for them.
watch(
	() => selectedProduct.value?.PartNo,
	() => {
		if (import.meta.client) initLipscore(true);
	}
);
</script>
